import React, { useEffect, useState } from 'react';
import { SkeletonStatCards, SkeletonChart } from '../../components/ui/Skeleton';
import { useLocation, useNavigate } from 'react-router-dom';
import AccountLayout from './AccountLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatusBadge from './components/StatusBadge';
import UsageBar from './components/UsageBar';
import { EmptyState, Button as A2Button } from '../../components/app2';
import AlertService from '../../utils/alertService';
import billingService, {
  describeStatus,
  formatMxn,
  formatDateLong,
  formatTierLabel,
} from '../../utils/billingService';

const AccountPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [stripeConfigured, setStripeConfigured] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle ?checkout=success or =cancelled query parameters returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      AlertService?.success(
        '¡Suscripción activada!',
        'Tu nuevo plan ya está activo. La información se actualizará en unos segundos.',
        { autoClose: true },
      );
      // Cleanup query string
      navigate('/app/account/plan', { replace: true });
    } else if (checkout === 'cancelled') {
      AlertService?.info?.('Checkout cancelado', 'No se realizó ningún cargo.', {
        autoClose: true,
      });
      navigate('/app/account/plan', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subRes, usageRes, invoicesRes] = await Promise.all([
        billingService.getMySubscription().catch((e) => ({ error: e })),
        billingService.getEmployeeUsage().catch((e) => ({ error: e })),
        billingService.listInvoices({ limit: 5 }).catch((e) => ({ error: e })),
      ]);

      if (subRes?.data) setSubscription(subRes.data);
      if (typeof subRes?.meta?.stripeConfigured === 'boolean') {
        setStripeConfigured(subRes.meta.stripeConfigured);
      }
      if (usageRes?.data) setUsage(usageRes.data);
      if (Array.isArray(invoicesRes?.data)) setInvoices(invoicesRes.data);
    } catch (err) {
      console.error('Error loading plan data:', err);
    } finally {
      setLoading(false);
    }
  };

  const status = describeStatus(subscription?.status);
  const plan = subscription?.plan;

  const handleManagePayment = async () => {
    try {
      setSubmitting(true);
      const { url } = await billingService.createPortalSession({
        returnUrl: window.location.href,
      });
      if (url) window.location.href = url;
    } catch (err) {
      console.error(err);
      AlertService?.error(
        'No se pudo abrir el portal',
        err?.response?.data?.error?.message || err?.message || 'Inténtalo más tarde.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await AlertService?.confirm(
      '¿Cancelar tu suscripción?',
      'Tu acceso seguirá activo hasta el final del período pagado. No se generarán reembolsos.',
      {
        confirmText: 'Sí, cancelar',
        cancelText: 'Conservar',
        confirmColor: '#dc2626',
      },
    );
    if (!confirmed) return;
    try {
      setSubmitting(true);
      await billingService.cancelSubscription();
      await AlertService?.success(
        'Suscripción cancelada',
        'Conservarás acceso hasta el final del período actual.',
        { autoClose: true },
      );
      loadData();
    } catch (err) {
      AlertService?.error(
        'No se pudo cancelar',
        err?.response?.data?.error?.message || err?.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setSubmitting(true);
      await billingService.reactivateSubscription();
      await AlertService?.success(
        'Suscripción reactivada',
        'Tu suscripción seguirá activa.',
        { autoClose: true },
      );
      loadData();
    } catch (err) {
      AlertService?.error(
        'No se pudo reactivar',
        err?.response?.data?.error?.message || err?.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showSubscribeCta =
    !subscription?.stripe_subscription_id || subscription?.status === 'no_subscription';

  return (
    <AccountLayout
      title="Mi plan"
      subtitle="Información sobre tu suscripción a PaySlip"
      headerActions={
        !loading && subscription ? <StatusBadge tone={status.tone} label={status.label} /> : null
      }
    >
      {loading ? (
        <div className="space-y-6">
          <SkeletonStatCards count={3} />
          <SkeletonChart />
        </div>
      ) : (
        <div className="space-y-6">
          {!stripeConfigured && (
            <div className="a2-note a2-note--warn">
              <Icon name="AlertTriangle" size={18} className="mt-0.5 flex-shrink-0" />
              <span>
                El sistema de cobros aún no está configurado en este ambiente. Si necesitas activar
                tu plan, contacta a soporte.
              </span>
            </div>
          )}

          {/* Past-due / payment failed banner */}
          {(subscription?.status === 'past_due' || subscription?.status === 'unpaid') && (
            <div className="a2-note a2-note--danger">
              <Icon name="AlertOctagon" size={18} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <strong className="block">Tu último pago no se procesó correctamente.</strong>
                <span>
                  Actualiza tu método de pago para mantener tu acceso. Después de varios intentos
                  fallidos la suscripción se cancelará automáticamente.
                </span>
              </div>
              <Button variant="primary" onClick={handleManagePayment} disabled={submitting}>
                Actualizar pago
              </Button>
            </div>
          )}

          {/* Cancellation pending banner */}
          {subscription?.cancel_at_period_end && (
            <div className="a2-note a2-note--warn">
              <Icon name="Clock" size={18} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <strong className="block">Cancelación programada</strong>
                <span>
                  Tu suscripción se cancelará el{' '}
                  <strong>{formatDateLong(subscription.current_period_end)}</strong>. Si cambias de
                  opinión puedes reactivarla.
                </span>
              </div>
              <Button variant="primary" onClick={handleReactivate} disabled={submitting}>
                Reactivar
              </Button>
            </div>
          )}

          {/* Overage banner */}
          {usage?.isOverage && (
            <div className="a2-note a2-note--warn">
              <Icon name="AlertTriangle" size={22} className="a2-t-warn flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold a2-t-heading">
                  Tienes más empleados que los permitidos en tu plan
                </p>
                <p className="text-sm a2-t-body mt-0.5">
                  Hay {usage.hiddenEmployees} empleado{usage.hiddenEmployees === 1 ? '' : 's'}{' '}
                  oculto{usage.hiddenEmployees === 1 ? '' : 's'} de un total de{' '}
                  {usage.activeEmployees}. Actualiza tu plan para verlos a todos.
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => navigate('/app/account/plans/upgrade')}
              >
                Cambiar plan
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Current plan card */}
            <div className="lg:col-span-2 a2-card a2-card-pad">
              {plan ? (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-medium a2-t-muted uppercase tracking-wide">
                        Plan actual
                      </p>
                      <h2 className="text-2xl font-semibold a2-t-heading mt-1">{plan.name}</h2>
                      <p className="text-sm a2-t-muted">{formatTierLabel(plan)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold a2-t-heading">
                        {formatMxn(Number(plan.price_mxn))}
                      </p>
                      <p className="text-xs a2-t-muted">/ mes (IVA inc.)</p>
                    </div>
                  </div>

                  <hr className="a2-hr my-5" />

                  <UsageBar
                    used={
                      usage?.visibleEmployees ??
                      Math.min(
                        usage?.activeEmployees ?? 0,
                        plan?.max_employees ?? Number.MAX_SAFE_INTEGER,
                      )
                    }
                    hidden={usage?.hiddenEmployees ?? 0}
                    limit={plan?.max_employees ?? null}
                  />

                  <hr className="a2-hr my-5" />

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="a2-t-muted">Próximo cobro</dt>
                      <dd className="font-medium a2-t-heading">
                        {subscription?.cancel_at_period_end
                          ? '—'
                          : formatDateLong(subscription?.current_period_end)}
                      </dd>
                    </div>
                    <div>
                      <dt className="a2-t-muted">Período actual</dt>
                      <dd className="font-medium a2-t-heading">
                        {formatDateLong(subscription?.current_period_start)} —{' '}
                        {formatDateLong(subscription?.current_period_end)}
                      </dd>
                    </div>
                    {subscription?.default_payment_method_last4 && (
                      <div>
                        <dt className="a2-t-muted">Método de pago</dt>
                        <dd className="font-medium a2-t-heading capitalize">
                          {subscription.default_payment_method_brand} ····{' '}
                          {subscription.default_payment_method_last4}
                          {subscription.default_payment_method_exp_month &&
                          subscription.default_payment_method_exp_year
                            ? ` (vence ${String(
                                subscription.default_payment_method_exp_month,
                              ).padStart(2, '0')}/${subscription.default_payment_method_exp_year})`
                            : ''}
                        </dd>
                      </div>
                    )}
                    {subscription?.applied_coupon?.code && (
                      <div>
                        <dt className="a2-t-muted">Cupón aplicado</dt>
                        <dd className="font-medium a2-t-heading">
                          {subscription.applied_coupon.code}
                        </dd>
                      </div>
                    )}
                  </dl>

                  <div className="flex flex-wrap gap-3 mt-6">
                    {showSubscribeCta ? (
                      <Button
                        variant="primary"
                        onClick={() => navigate('/app/account/plans/upgrade')}
                      >
                        <Icon name="CreditCard" size={16} className="mr-2" />
                        Elegir plan
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          onClick={() => navigate('/app/account/plans/upgrade')}
                        >
                          <Icon name="TrendingUp" size={16} className="mr-2" />
                          Cambiar plan
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleManagePayment}
                          disabled={submitting}
                        >
                          <Icon name="CreditCard" size={16} className="mr-2" />
                          Gestionar pago
                        </Button>
                        {subscription?.cancel_at_period_end ? null : (
                          <Button
                            variant="destructive"
                            onClick={handleCancel}
                            disabled={submitting}
                          >
                            <Icon name="X" size={16} className="mr-2" />
                            Cancelar suscripción
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <EmptyState
                  icon="workspace_premium"
                  title="No tienes plan activo"
                  text="Elige un plan para empezar a usar PaySlip sin restricciones."
                  className="a2-card"
                  style={{ border: 'none' }}
                  action={
                    <A2Button icon="credit_card" onClick={() => navigate('/app/account/plans/upgrade')}>
                      Ver planes
                    </A2Button>
                  }
                />
              )}
            </div>

            {/* Recent invoices summary */}
            <div className="a2-card a2-card-pad">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold a2-t-heading">Últimas facturas</h3>
                <button
                  onClick={() => navigate('/app/account/billing')}
                  className="text-sm a2-t-brand hover:underline"
                >
                  Ver todas
                </button>
              </div>
              {invoices.length === 0 ? (
                <p className="text-sm a2-t-muted">Aún no hay facturas.</p>
              ) : (
                <ul className="a2-divide">
                  {invoices.slice(0, 5).map((inv) => (
                    <li key={inv.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium a2-t-heading truncate">
                          {inv.number || `Factura #${inv.id}`}
                        </p>
                        <p className="text-xs a2-t-muted">
                          {formatDateLong(inv.paid_at || inv.period_start)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold a2-t-heading">
                          {formatMxn(inv.amount_paid)}
                        </p>
                        <StatusBadge
                          tone={
                            inv.status === 'paid'
                              ? 'success'
                              : inv.status === 'open' || inv.status === 'draft'
                              ? 'warning'
                              : 'muted'
                          }
                          label={inv.status}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </AccountLayout>
  );
};

export default AccountPlan;
